import { Period } from '../types/dashboard'

interface DateRangeCalculator {
  (
    now: Date,
    addDays: (date: Date, days: number) => Date,
    startOfDay: (date: Date) => Date,
    endOfDay: (date: Date) => Date
  ): { start: Date; end: Date }
}

interface PeriodConfig {
  label: string
  previousLabel: string
  calculate: DateRangeCalculator
}

const toLocalISOString = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  const ms = String(date.getMilliseconds()).padStart(3, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`
}

const startOfDay = (date: Date): Date => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

const endOfDay = (date: Date): Date => {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

const getMondayOfWeek = (date: Date): Date => {
  const dayOfWeek = date.getDay()
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  return addDays(date, -daysSinceMonday)
}

const PERIOD_CONFIGS: Record<Period, PeriodConfig> = {
  [Period.Today]: {
    label: 'Hoje',
    previousLabel: 'vs Ontem',
    calculate: (now) => ({
      start: startOfDay(now),
      end: endOfDay(now),
    }),
  },

  [Period.Yesterday]: {
    label: 'Ontem',
    previousLabel: 'vs Anteontem',
    calculate: (now) => {
      const yesterday = addDays(now, -1)
      return {
        start: startOfDay(yesterday),
        end: endOfDay(yesterday),
      }
    },
  },

  [Period.ThisWeek]: {
    label: 'Este Ciclo',
    previousLabel: 'vs Ciclo Passado',
    calculate: (now) => ({
      start: startOfDay(getMondayOfWeek(now)),
      end: endOfDay(now),
    }),
  },

  [Period.LastWeek]: {
    label: 'Ciclo Passado',
    previousLabel: 'vs Ciclo Anterior',
    calculate: (now) => {
      const lastWeekEnd = addDays(getMondayOfWeek(now), -1)
      const lastWeekStart = addDays(lastWeekEnd, -6)
      return {
        start: startOfDay(lastWeekStart),
        end: endOfDay(lastWeekEnd),
      }
    },
  },

  [Period.ThisMonth]: {
    label: 'Este Mês',
    previousLabel: 'vs Mês Passado',
    calculate: (now) => ({
      start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
      end: endOfDay(now),
    }),
  },

  [Period.LastMonth]: {
    label: 'Mês Passado',
    previousLabel: 'vs Mês Anterior',
    calculate: (now) => {
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0)
      return {
        start: startOfDay(lastMonthStart),
        end: endOfDay(lastMonthEnd),
      }
    },
  },

  [Period.ThisYear]: {
    label: 'Este Ano',
    previousLabel: 'vs Ano Passado',
    calculate: (now) => ({
      start: startOfDay(new Date(now.getFullYear(), 0, 1)),
      end: endOfDay(now),
    }),
  },

  [Period.LastYear]: {
    label: 'Ano Passado',
    previousLabel: 'vs Ano Anterior',
    calculate: (now) => ({
      start: startOfDay(new Date(now.getFullYear() - 1, 0, 1)),
      end: endOfDay(new Date(now.getFullYear() - 1, 11, 31)),
    }),
  },

  [Period.Custom]: {
    label: 'Personalizado',
    previousLabel: 'vs Período Anterior',
    calculate: (now) => ({
      start: startOfDay(now),
      end: endOfDay(now),
    }),
  },
}

const PREVIOUS_PERIOD_STRATEGIES: Record<
  Period,
  (now: Date) => { start: Date; end: Date }
> = {
  [Period.Today]: (now) => {
    const yesterday = addDays(now, -1)
    return {
      start: startOfDay(yesterday),
      end: endOfDay(yesterday),
    }
  },

  [Period.Yesterday]: (now) => {
    const twoDaysAgo = addDays(now, -2)
    return {
      start: startOfDay(twoDaysAgo),
      end: endOfDay(twoDaysAgo),
    }
  },

  [Period.ThisWeek]: (now) => {
    const lastWeekStart = addDays(getMondayOfWeek(now), -7)
    const lastWeekEnd = addDays(lastWeekStart, 6)
    return {
      start: startOfDay(lastWeekStart),
      end: endOfDay(lastWeekEnd),
    }
  },

  [Period.LastWeek]: (now) => {
    const twoWeeksAgoStart = addDays(getMondayOfWeek(now), -14)
    const twoWeeksAgoEnd = addDays(twoWeeksAgoStart, 6)
    return {
      start: startOfDay(twoWeeksAgoStart),
      end: endOfDay(twoWeeksAgoEnd),
    }
  },

  [Period.ThisMonth]: (now) => {
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0)
    return {
      start: startOfDay(lastMonth),
      end: endOfDay(lastMonthEnd),
    }
  },

  [Period.LastMonth]: (now) => {
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1)
    const twoMonthsAgoEnd = new Date(now.getFullYear(), now.getMonth() - 1, 0)
    return {
      start: startOfDay(twoMonthsAgo),
      end: endOfDay(twoMonthsAgoEnd),
    }
  },

  [Period.ThisYear]: (now) => ({
    start: startOfDay(new Date(now.getFullYear() - 1, 0, 1)),
    end: endOfDay(new Date(now.getFullYear() - 1, 11, 31)),
  }),

  [Period.LastYear]: (now) => ({
    start: startOfDay(new Date(now.getFullYear() - 2, 0, 1)),
    end: endOfDay(new Date(now.getFullYear() - 2, 11, 31)),
  }),

  [Period.Custom]: (now) => {
    const yesterday = addDays(now, -1)
    return {
      start: startOfDay(yesterday),
      end: endOfDay(yesterday),
    }
  },
}

export const getPeriodLabel = (period: Period): string => {
  return PERIOD_CONFIGS[period]?.label || period
}

export const getPreviousPeriodLabel = (period: Period): string => {
  return PERIOD_CONFIGS[period]?.previousLabel || period
}

export const getPeriodDateRange = (
  period: Period,
  customDates?: { startDate: string; endDate: string }
): { start: string; end: string } => {
  const now = new Date()

  if (
    period === Period.Custom &&
    customDates?.startDate &&
    customDates?.endDate
  ) {
    const [startYear, startMonth, startDay] = customDates.startDate
      .split('-')
      .map(Number)
    const [endYear, endMonth, endDay] = customDates.endDate
      .split('-')
      .map(Number)

    const customStart = new Date(startYear, startMonth - 1, startDay)
    const customEnd = new Date(endYear, endMonth - 1, endDay)

    return {
      start: toLocalISOString(startOfDay(customStart)),
      end: toLocalISOString(endOfDay(customEnd)),
    }
  }

  const config = PERIOD_CONFIGS[period]
  const { start, end } = config.calculate(now, addDays, startOfDay, endOfDay)

  return {
    start: toLocalISOString(start),
    end: toLocalISOString(end),
  }
}

export const getPreviousPeriodDateRange = (
  period: Period,
  customDates?: { startDate: string; endDate: string }
): { start: string; end: string } => {
  const now = new Date()

  if (
    period === Period.Custom &&
    customDates?.startDate &&
    customDates?.endDate
  ) {
    const customStart = new Date(customDates.startDate)
    const customEnd = new Date(customDates.endDate)
    const diffDays = Math.ceil(
      (customEnd.getTime() - customStart.getTime()) / (1000 * 60 * 60 * 24)
    )
    const previousEnd = addDays(customStart, -1)
    const previousStart = addDays(previousEnd, -diffDays)

    return {
      start: toLocalISOString(startOfDay(previousStart)),
      end: toLocalISOString(endOfDay(previousEnd)),
    }
  }

  const strategy = PREVIOUS_PERIOD_STRATEGIES[period]
  const { start, end } = strategy(now)

  return {
    start: toLocalISOString(start),
    end: toLocalISOString(end),
  }
}
